import { useCallback, useEffect, useRef, useState } from "react";
import { useWorkspace } from "contexts/WorkspaceContext";
import { olreadiaService } from "services/olreadiaService";
import { useAuth } from "contexts/AuthContext";
import { identitySubscriptionService } from "services/subscriptions";

const PLAN_LIMITS = {
  FREE: 10,
  STANDARD: 100,
  PREMIUM: 1000,
};

function resolveLimit(planCodes) {
  if (!planCodes || planCodes.length === 0) return PLAN_LIMITS.FREE;
  const upper = planCodes.map((c) => c.toUpperCase());
  if (upper.some((c) => c.includes("PREMIUM"))) return PLAN_LIMITS.PREMIUM;
  if (upper.some((c) => c.includes("STANDARD"))) return PLAN_LIMITS.STANDARD;
  return PLAN_LIMITS.FREE;
}

function resolvePlanCode(planCodes) {
  if (!planCodes || planCodes.length === 0) return "FREE";
  const upper = planCodes.map((c) => c.toUpperCase());
  if (upper.some((c) => c.includes("PREMIUM"))) return "PREMIUM";
  if (upper.some((c) => c.includes("STANDARD"))) return "STANDARD";
  return "FREE";
}

export default function useAiRequestUsage(workspaceId) {
  const { user } = useAuth();
  const actorId = user?.id || user?.userId;
  const [data, setData] = useState({
    used: 0,
    limit: null,
    planCode: null,
    remaining: null,
    loading: true,
    error: null,
    hasReachedLimit: false,
  });
  const intervalRef = useRef(null);

  const refetch = useCallback(async () => {
    if (!workspaceId) {
      setData({
        used: 0,
        limit: 10,
        planCode: "FREE",
        remaining: 10,
        loading: false,
        error: null,
        hasReachedLimit: false,
      });
      return;
    }

    try {
      const [usageResult, planCodes] = await Promise.all([
        olreadiaService.usage.aiRequests(workspaceId, actorId).catch(() => null),
        identitySubscriptionService.plans
          .list({ code: "OLREADIA", status: "ACTIVE" })
          .then((res) => {
            const content = res?.data?.content || res?.content || [];
            return content.map((p) => p.code || p.name || "");
          })
          .catch(() => []),
      ]);

      const limit = usageResult?.limit ?? resolveLimit(planCodes);
      const used = usageResult?.used ?? 0;
      const planCode = usageResult?.planCode ?? resolvePlanCode(planCodes);
      // If usage endpoint returned data, trust it; otherwise guess from plan codes
      const effectiveLimit = usageResult ? usageResult.limit : limit;
      const effectiveUsed = usageResult ? usageResult.used : 0;

      // Preserve null limit (truly unlimited) — otherwise compute remaining
      const finalLimit = effectiveLimit === null ? null : effectiveLimit;
      const remaining = finalLimit === null ? null : Math.max(0, finalLimit - effectiveUsed);
      const hasReachedLimit = finalLimit !== null && effectiveUsed >= finalLimit;

      setData({
        used: effectiveUsed,
        limit: finalLimit,
        planCode,
        remaining,
        loading: false,
        error: null,
        hasReachedLimit,
      });
    } catch (err) {
      // On error, keep last known values; mark not loading
      setData((prev) => ({ ...prev, loading: false, error: err?.message || "Failed to fetch usage" }));
    }
  }, [workspaceId, actorId]);

  useEffect(() => {
    refetch();

    // Poll every 60s to keep usage in sync
    intervalRef.current = setInterval(refetch, 60_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refetch]);

  return { ...data, refetch };
}
